import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Member/SubscriptionMobilePricing/options";
import { formatCurrencyAmount, getCurrencySymbolFromCode } from "utils";

import type { PricingOptionItem } from "./types";
import { ProductName } from "@/src/components/Member/CurrentPlan/constants";
import {
  BillingCycle,
  DiscountValueMaps,
} from "@/src/components/Member/Subscription/constants";
import { MemberProduct } from "@/src/features/api/memberApi";

const I18n = I18nUtil.createScoped(Translations);

type Unit = "/wk" | "/mo" | "/yr" | "";

// 把价格拆成金额+单位，供 UI 分开排版（金额大字，单位小字）。
const createPriceParts = (
  amount: number,
  unit: Unit,
  locale?: string,
): Pick<
  PricingOptionItem,
  "pricePrefixText" | "priceAmountValue" | "priceAmountText" | "priceUnitText"
> => ({
  pricePrefixText: getCurrencySymbolFromCode(undefined, locale),
  priceAmountValue: amount,
  priceAmountText: new Intl.NumberFormat(locale || undefined, {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount),
  priceUnitText: unit,
});

// 兼容旧版折扣映射：后端无显式折扣时，按历史配置推导展示折扣。
const getLegacyDiscountPercent = (plan?: MemberProduct): number | undefined => {
  const discountPercent =
    DiscountValueMaps[
      plan?.name as ProductName as keyof typeof DiscountValueMaps
    ]?.[plan?.pay_cycle as typeof BillingCycle.MONTH];

  if (!Number.isFinite(discountPercent)) {
    return undefined;
  }

  return Number(discountPercent);
};

// 通过现价 + 折扣反推原价，仅用于需要展示“划线原价”的场景。
const getLegacyOriginalPriceFromDiscount = (
  plan?: MemberProduct,
): number | undefined => {
  const price = Number(plan?.price);
  const discountPercent = getLegacyDiscountPercent(plan);

  if (!Number.isFinite(price) || !discountPercent || discountPercent >= 100) {
    return undefined;
  }

  return price / (1 - discountPercent / 100);
};

// 统一原价文案分发：
// - legacyDiscount: 走“反推原价”；
// - currentRounded: 走“当前价取整展示”（周订阅要求）；
// - none: 不展示原价（终身）。
const resolveOriginalPriceText = ({
  price,
  legacyOriginalPrice,
  rule,
  unit,
}: {
  price?: number;
  legacyOriginalPrice?: number;
  rule: "legacyDiscount" | "currentRounded" | "none";
  unit: Unit;
}): string | undefined => {
  if (rule === "legacyDiscount") {
    if (!legacyOriginalPrice) {
      return undefined;
    }
    return `$${legacyOriginalPrice.toFixed(0)}${unit}`;
  }

  if (rule === "currentRounded") {
    if (typeof price !== "number" || !Number.isFinite(price)) {
      return undefined;
    }
    return `$${price.toFixed(0)}${unit}`;
  }

  return undefined;
};

const DISCOUNT_BY_SOURCE: Record<
  "legacy" | "none",
  (plan?: MemberProduct) => number
> = {
  legacy: (plan) => getLegacyDiscountPercent(plan) || 0,
  none: () => 0,
};

const SAVE_TEXT_BY_RULE: Record<
  "discount" | "bestValue" | "limited" | "none",
  (params: {
    locale?: string;
    discountPercent: number;
    limitCount: number;
  }) => string | undefined
> = {
  discount: ({ discountPercent, locale }) =>
    discountPercent > 0
      ? I18n.t("Save %{var0}%", { locale, var0: discountPercent.toFixed(0) })
      : undefined,
  bestValue: ({ locale }) => I18n.t("Best Value", { locale }),
  limited: ({ locale, limitCount }) =>
    I18n.t("Limited to the first %{var0} members", {
      locale,
      var0: limitCount,
    }),
  none: () => undefined,
};

const ORIGINAL_PRICE_BY_RULE: Record<
  "legacyDiscount" | "currentRounded" | "none",
  (params: {
    locale?: string;
    price: number;
    legacyOriginalPrice?: number;
    unit: Unit;
  }) => string | undefined
> = {
  legacyDiscount: ({ legacyOriginalPrice, unit }) =>
    resolveOriginalPriceText({
      legacyOriginalPrice,
      rule: "legacyDiscount",
      unit,
    }),
  currentRounded: ({ price, unit }) =>
    resolveOriginalPriceText({
      price,
      rule: "currentRounded",
      unit,
    }),
  none: () => undefined,
};

// Trial badge 文案策略：
// - iOS 永远显示 free（与平台策略一致）；
// - 其他端优先显示试用价；
// - 都拿不到时回退 free 文案。
const getTrialBadgeText = ({
  plan,
  trialList,
  isMember,
  isIOSApp,
  locale,
}: {
  plan?: MemberProduct;
  trialList?: MemberProduct[];
  isMember?: boolean;
  isIOSApp: boolean;
  locale?: string;
}): string | undefined => {
  if (!plan?.id || isMember) {
    return undefined;
  }
  const matchedTrial = (trialList || []).find(
    (trialPlan) => trialPlan.renewal_id === plan.id,
  );
  if (!matchedTrial) {
    return undefined;
  }

  if (isIOSApp) {
    return I18n.t("New users can try for free", { locale });
  }

  const trialPrice = Number(matchedTrial.price);
  if (Number.isFinite(trialPrice)) {
    return I18n.t("New users can try for %{var0}", {
      locale,
      var0: formatCurrencyAmount(trialPrice),
    });
  }

  return I18n.t("New users can try for free", { locale });
};

export const buildPricingOptions = ({
  selectedWeeklyPlan,
  selectedMonthlyPlan,
  selectedYearlyPlan,
  selectedLifetimePlan,
  trialList,
  isMember,
  isIOSApp,
  locale,
}: {
  selectedWeeklyPlan?: MemberProduct;
  selectedMonthlyPlan?: MemberProduct;
  selectedYearlyPlan?: MemberProduct;
  selectedLifetimePlan?: MemberProduct;
  trialList?: MemberProduct[];
  isMember?: boolean;
  isIOSApp: boolean;
  locale?: string;
}): PricingOptionItem[] => {
  type PlanConfig = {
    key: PricingOptionItem["key"];
    title: string;
    unit: Unit;
    plan?: MemberProduct;
    discountSource: "legacy" | "none";
    originalPriceRule: "legacyDiscount" | "currentRounded" | "none";
    saveTextRule: "discount" | "bestValue" | "limited" | "none";
    showTrialBadge: boolean;
  };

  // 单一配置入口：不同 plan 的差异通过规则表达，避免分散 if/switch。
  const planConfigs: PlanConfig[] = [
    {
      key: "weekly" as const,
      title: I18n.t("Weekly", { locale }),
      unit: "/wk" as const,
      plan: selectedWeeklyPlan,
      discountSource: "none",
      originalPriceRule: "none",
      saveTextRule: "none",
      showTrialBadge: false,
    },
    {
      key: "monthly" as const,
      title: I18n.t("Monthly", { locale }),
      unit: "/mo" as const,
      plan: selectedMonthlyPlan,
      discountSource: "none",
      originalPriceRule: "none",
      saveTextRule: "discount",
      showTrialBadge: true,
    },
    {
      key: "yearly" as const,
      title: I18n.t("Annual", { locale }),
      unit: "/yr" as const,
      plan: selectedYearlyPlan,
      discountSource: "legacy",
      originalPriceRule: "legacyDiscount",
      saveTextRule: "discount",
      showTrialBadge: true,
    },
    {
      key: "lifetime" as const,
      title: I18n.t("Lifetime", { locale }),
      unit: "" as const,
      plan: selectedLifetimePlan,
      discountSource: "none",
      originalPriceRule: "none",
      saveTextRule: "limited",
      showTrialBadge: false,
    },
  ];

  return planConfigs.flatMap((config) => {
    const price = Number(config.plan?.price);
    // 后端缺失或异常价格直接跳过，避免抛错影响页面渲染。
    if (!Number.isFinite(price) || price <= 0) {
      return [];
    }

    const discountPercent = DISCOUNT_BY_SOURCE[config.discountSource](
      config.plan,
    );
    const legacyOriginalPrice =
      config.originalPriceRule === "legacyDiscount"
        ? getLegacyOriginalPriceFromDiscount(config.plan)
        : undefined;

    const trialBadgeText = config.showTrialBadge
      ? getTrialBadgeText({
          plan: config.plan,
          trialList,
          isMember,
          isIOSApp,
        })
      : undefined;
    const saveText = SAVE_TEXT_BY_RULE[config.saveTextRule]({
      locale,
      discountPercent,
      limitCount: 2000,
    });
    const originalPriceText = ORIGINAL_PRICE_BY_RULE[config.originalPriceRule]({
      locale,
      price,
      legacyOriginalPrice,
      unit: config.unit,
    });

    return [
      {
        key: config.key,
        plan_id: config.plan?.id,
        title: config.title,
        ...createPriceParts(price, config.unit, locale),
        trialBadgeText,
        saveText,
        originalPriceText,
      },
    ];
  });
};
